import { useCallback, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'sonner';
import {
  intakeChangeRequestApi,
  intakeChangeRequestKeys,
  quickEditApi,
} from '@client/api/waterlily';
import { fetchClientByClientIdRequest } from '@client/model/clientModel';
import { fetchTypeformPayloadByClientIdRequest } from '@client/model/typeformPayloadModel';
import { fetchClientsForAdvisorByAdvisorIdRequest } from '@client/model/clientsModel';
import { selectSessionAdvisor } from '@client/model/selectors';

interface UseQuickEditIntakeOptions {
  clientId: string;
  checkApprovalPolicy?: boolean;
  onSaveSuccess?: () => void;
  successMessage?: string;
}

export function useQuickEditIntake({
  clientId,
  checkApprovalPolicy = true,
  onSaveSuccess,
  successMessage = 'Intake form updated successfully.',
}: UseQuickEditIntakeOptions) {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const advisor = useSelector(selectSessionAdvisor);
  const [mode, setMode] = useState<'view' | 'edit'>('view');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [editingRequestId, setEditingRequestId] = useState<string | null>(null);
  const [pendingApprovalFields, setPendingApprovalFields] = useState<Record<
    string,
    string | number | null
  > | null>(null);
  const stateQuery = useQuery({
    queryKey: intakeChangeRequestKeys.detail(clientId),
    queryFn: () => quickEditApi.getState(clientId),
    enabled: checkApprovalPolicy,
  });

  const mutation = useMutation({
    mutationFn: ({
      fields,
      requestId,
    }: {
      fields: Record<string, string | number | null>;
      requestId?: string;
    }) => quickEditApi.save(clientId, fields, requestId),
    onSuccess: response => {
      void queryClient.invalidateQueries({ queryKey: intakeChangeRequestKeys.detail(clientId) });
      if ('withdrawn' in response) {
        toast.success('Request withdrawn.');
        setEditingRequestId(null);
        setMode('view');
        return;
      }
      if ('pending' in response) {
        toast.success('Request sent to the client for approval.');
        setEditingRequestId(null);
        setMode('view');
        onSaveSuccess?.();
        return;
      }
      setIsRefreshing(true);
      dispatch(fetchClientByClientIdRequest(clientId));
      dispatch(fetchTypeformPayloadByClientIdRequest({ clientId }));
      if (advisor?.advisorId) {
        dispatch(
          fetchClientsForAdvisorByAdvisorIdRequest({
            advisorId: advisor.advisorId,
            isInvalidation: true,
          }),
        );
      }
      toast.success(successMessage);
      setMode('view');
      onSaveSuccess?.();
    },
    onError: (error: unknown) => {
      const errorMessage = error instanceof Error ? error.message : 'Failed to update intake form.';
      toast.error(errorMessage);
    },
  });
  const withdrawal = useMutation({
    mutationFn: (requestId: string) => intakeChangeRequestApi.withdraw(clientId, requestId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: intakeChangeRequestKeys.detail(clientId) });
      setEditingRequestId(null);
      setMode('view');
      toast.success('Request withdrawn.');
    },
    onError: (error: unknown) => {
      toast.error(error instanceof Error ? error.message : 'Failed to withdraw the request.');
    },
  });
  const recalculation = useMutation({
    mutationFn: (requestId: string) => intakeChangeRequestApi.recalculate(clientId, requestId),
    onSuccess: () => {
      toast.success('Plan recalculated.');
    },
    onError: (error: unknown) => {
      toast.error(
        error instanceof Error
          ? error.message
          : 'The plan could not be recalculated. Try again or contact support.',
      );
    },
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey: intakeChangeRequestKeys.detail(clientId) }),
  });

  const clearRefreshing = useCallback(() => {
    setIsRefreshing(false);
  }, []);

  const save = useCallback(
    (fields: Record<string, string | number | null>) => {
      if (!checkApprovalPolicy) {
        mutation.mutate({ fields });
        return;
      }
      if (!stateQuery.isSuccess) {
        toast.error('Please wait while we verify how this change should be saved.');
        return;
      }
      if (stateQuery.data.policy === 'approval-required') {
        setPendingApprovalFields(fields);
        return;
      }
      mutation.mutate({ fields });
    },
    [checkApprovalPolicy, mutation, stateQuery.data, stateQuery.isSuccess],
  );

  const confirmApprovalRequest = useCallback(() => {
    if (!pendingApprovalFields) return;
    mutation.mutate({ fields: pendingApprovalFields, requestId: editingRequestId ?? undefined });
    setPendingApprovalFields(null);
  }, [editingRequestId, mutation, pendingApprovalFields]);

  const cancelApprovalRequest = useCallback(() => {
    setPendingApprovalFields(null);
  }, []);

  return {
    mode,
    setMode,
    save,
    isPending: mutation.isPending,
    isRefreshing,
    clearRefreshing,
    isError: mutation.isError,
    approvalPolicy: stateQuery.data?.policy,
    intakeChangeRequest: stateQuery.data?.request ?? null,
    latestApprovedRequest: stateQuery.data?.latestApprovedRequest ?? null,
    isLoadingIntakeChangeRequest: stateQuery.isLoading,
    isApprovalPolicyReady: !checkApprovalPolicy || stateQuery.isSuccess,
    isApprovalPromptOpen: pendingApprovalFields !== null,
    confirmApprovalRequest,
    cancelApprovalRequest,
    editingRequestId,
    editProposal: (requestId: string) => {
      setEditingRequestId(requestId);
      setMode('edit');
    },
    withdrawRequest: (requestId: string) => withdrawal.mutate(requestId),
    isWithdrawing: withdrawal.isPending,
    recalculateRequest: (requestId: string) => recalculation.mutate(requestId),
    isRecalculatingRequest: recalculation.isPending,
    recalculationError: recalculation.error,
  };
}
